import { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, GitCompareArrows, Trophy, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { calculate } from "@/lib/api";
import { moneyFull, pct, months } from "@/lib/format";
import { Button } from "@/components/ui/button";
import CompareInputs from "@/components/CompareInputs";
import SensitivityChart from "@/components/SensitivityChart";
import CurrencyToggle from "@/components/CurrencyToggle";
import LanguageToggle from "@/components/LanguageToggle";
import AppFooter from "@/components/AppFooter";
import { detectLocalCurrency, useCurrencyRates, convertInputs, convertBenchmarkOverrides } from "@/lib/currency";

const DEFAULT_SIDE = (label) => ({
  label,
  model_type: "b2c",
  mode: "quick",
  industry: "saas",
  wire_tier: "standard",
  region: "north_america",
  da_range: "30-50",
  credibility: "medium",
  inputs: {
    campaign_name: label,
    baseline_traffic: 50000,
    lift_pct: 20,
    conv_rate: 2.5,
    aov: 250,
    ltv: 900,
    gross_margin: 60,
    timeframe_months: 3,
    risk_reduction_value: 0,
    costs: { writing: 800, agency: 1500, distribution: 750, design: 400, paid_amplification: 1000, tracking: 200 },
  },
  benchmarks: {},
});

export default function Compare() {
  const [currency, setCurrency] = useState("USD");
  const [sideA, setSideA] = useState(DEFAULT_SIDE("Option A"));
  const [sideB, setSideB] = useState({
    ...DEFAULT_SIDE("Option B"),
    wire_tier: "premium",
    inputs: {
      ...DEFAULT_SIDE("Option B").inputs,
      costs: { writing: 800, agency: 1500, distribution: 2500, design: 400, paid_amplification: 2000, tracking: 200 },
    },
  });
  const [resA, setResA] = useState(null);
  const [resB, setResB] = useState(null);
  const [loading, setLoading] = useState(false);
  const { rates, asOf, source, formatDate } = useCurrencyRates();
  const detectedOnce = useRef(false);

  useEffect(() => {
    if (detectedOnce.current) return;
    detectedOnce.current = true;
    const detected = detectLocalCurrency();
    if (detected && detected !== currency) setCurrency(detected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleCurrencyChange(next) {
    if (next === currency) return;
    const prev = currency;
    setSideA((s) => ({ ...s, inputs: convertInputs(s.inputs, prev, next, rates), benchmarks: convertBenchmarkOverrides(s.benchmarks, prev, next, rates) }));
    setSideB((s) => ({ ...s, inputs: convertInputs(s.inputs, prev, next, rates), benchmarks: convertBenchmarkOverrides(s.benchmarks, prev, next, rates) }));
    setCurrency(next);
    toast.success(`Converted ${prev} → ${next}`, { description: `ECB rate as of ${formatDate(asOf)}` });
  }

  const payload = (side) => ({
    model_type: side.model_type,
    currency,
    mode: side.mode,
    industry: side.industry,
    wire_tier: side.wire_tier,
    region: side.region,
    da_range: side.da_range,
    credibility: side.credibility,
    inputs: side.inputs,
    benchmarks: side.benchmarks,
  });

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const [a, b] = await Promise.all([calculate(payload(sideA)), calculate(payload(sideB))]);
        setResA(a.results);
        setResB(b.results);
      } catch (e) {
        toast.error("Comparison failed", { description: e?.message });
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(sideA), JSON.stringify(sideB), currency]);

  const diff = useMemo(() => {
    if (!resA || !resB) return null;
    const a = resA.base, b = resB.base;
    const winner = a.roi_pct === b.roi_pct ? "TIE" : a.roi_pct > b.roi_pct ? "A" : "B";
    return {
      winner,
      roi_delta: a.roi_pct - b.roi_pct,
      net_delta: a.net_benefit - b.net_benefit,
      cost_delta: a.total_cost - b.total_cost,
      benefit_delta: a.total_benefit - b.total_benefit,
    };
  }, [resA, resB]);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-20">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.24em] font-semibold">PressImpact</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-slate-500 font-semibold">
              <GitCompareArrows className="h-4 w-4" /> Compare mode
            </span>
            <LanguageToggle />
            <CurrencyToggle
              value={currency}
              onChange={handleCurrencyChange}
              rates={rates}
              asOf={asOf}
              source={source}
              formatDate={formatDate}
              testPrefix="compare-"
            />
            <Link to="/calculator">
              <Button variant="outline" className="rounded-sm border-slate-200">Single analysis</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 py-8 space-y-8">
        {/* Verdict band */}
        {diff && resA && resB && (
          <section
            data-testid="compare-verdict"
            className="border border-slate-200 bg-white p-8 print-break-avoid"
          >
            <div className="flex items-start justify-between flex-wrap gap-6">
              <div>
                <p className="text-xs tracking-[0.28em] uppercase text-slate-500 mb-3">Verdict</p>
                <div className="flex items-center gap-4">
                  <Trophy className="h-8 w-8 text-slate-900" strokeWidth={1.5} />
                  <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-slate-900">
                    {diff.winner === "TIE" ? "Dead heat" : (
                      <>
                        <span className="text-slate-900">{diff.winner === "A" ? sideA.inputs.campaign_name || "Option A" : sideB.inputs.campaign_name || "Option B"}</span>
                        <span className="text-slate-400"> wins</span>
                      </>
                    )}
                  </h2>
                </div>
                {loading && <p className="text-[10px] mono uppercase tracking-widest text-slate-400 mt-2">recalculating</p>}
              </div>
              <div className="grid grid-cols-3 gap-6">
                <DeltaCell label="ROI Δ" a={resA.base.roi_pct} b={resB.base.roi_pct} format={pct} unit="%" />
                <DeltaCell label="Net Δ" a={resA.base.net_benefit} b={resB.base.net_benefit} format={(v) => moneyFull(v, currency)} />
                <DeltaCell label="Cost Δ" a={resA.base.total_cost} b={resB.base.total_cost} format={(v) => moneyFull(v, currency)} invertGood />
              </div>
            </div>
          </section>
        )}

        {/* Two columns */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <SideColumn side={sideA} setSide={setSideA} results={resA} currency={currency} tag="A" accent="#0055FF" testPrefix="a" />
          <SideColumn side={sideB} setSide={setSideB} results={resB} currency={currency} tag="B" accent="#10B981" testPrefix="b" />
        </section>
      </main>
      <AppFooter />
    </div>
  );
}

function DeltaCell({ label, a, b, format, unit = "", invertGood = false }) {
  const delta = a - b;
  const good = invertGood ? delta < 0 : delta > 0;
  const color = delta === 0 ? "text-slate-500" : good ? "text-emerald-600" : "text-red-600";
  const arrow = delta === 0 ? "" : delta > 0 ? "▲" : "▼";
  return (
    <div className="min-w-[140px]">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className={`font-heading text-2xl font-light mt-1 mono ${color}`}>
        {arrow} {format(Math.abs(delta))}{unit && Math.abs(delta) === Math.abs(delta) ? "" : ""}
      </div>
    </div>
  );
}

function SideColumn({ side, setSide, results, currency, tag, accent, testPrefix }) {
  const base = results?.base;
  const roiPositive = base ? base.roi_pct >= 0 : true;
  return (
    <div className="border border-slate-200 bg-white">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200" style={{ background: `${accent}0d` }}>
        <div className="flex items-center gap-3">
          <span className="font-heading text-xs uppercase tracking-[0.28em] font-semibold" style={{ color: accent }}>Option {tag}</span>
          <input
            data-testid={`${testPrefix}-campaign-name`}
            value={side.inputs.campaign_name}
            onChange={(e) => setSide({ ...side, inputs: { ...side.inputs, campaign_name: e.target.value } })}
            className="font-heading text-lg bg-transparent focus:outline-none border-b border-transparent focus:border-slate-300 min-w-[200px]"
            placeholder={`Option ${tag}`}
          />
        </div>
        {base && (
          <div className="text-right">
            <div className={`font-heading text-3xl font-light ${roiPositive ? "text-slate-900" : "text-red-600"}`} data-testid={`${testPrefix}-roi`}>
              {pct(base.roi_pct)}
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1 mono">
              {moneyFull(base.net_benefit, currency)} net
            </div>
          </div>
        )}
      </div>

      {results && (
        <div className="px-6 pt-4 pb-2 grid grid-cols-3 gap-3 border-b border-slate-100">
          <MiniStat label="Benefit" value={moneyFull(base.total_benefit, currency)} />
          <MiniStat label="Cost" value={moneyFull(base.total_cost, currency)} />
          <MiniStat label="Payback" value={base.payback_beyond_timeframe && base.payback_months ? `> ${side.inputs.timeframe_months} mo` : months(base.payback_months)} />
        </div>
      )}

      {results && (
        <div className="px-6 py-4 border-b border-slate-100">
          <SensitivityChart results={results} currency={currency} height={120} />
        </div>
      )}

      <div className="p-6">
        <CompareInputs side={side} setSide={setSide} testPrefix={testPrefix} />
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="text-sm font-medium text-slate-900 mono mt-1">{value}</div>
    </div>
  );
}
